import { useState, useEffect, useRef } from "react";
import { t } from "../i18n";

export default function Navbar({
  lang,
  user,
  showUserMenu,
  setShowUserMenu,
  userInitials,
  isAdmin,
  onOpenUserLibrary,
  onLogout,
  onToggleLang,
  onNavigate,
  onResetFilters,
  onOpenEmailLogin,
}) {
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const langRef = useRef(null);
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    if (!showLangMenu) return;
    const handler = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setShowLangMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showLangMenu]);

  useEffect(() => {
    if (!showUserMenu) return;
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showUserMenu, setShowUserMenu]);

  useEffect(() => {
    if (!showMobileMenu) return;
    const handler = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMobileMenu]);

  return (
    <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={onResetFilters}
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="2.5"
            >
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
          </div>
          <span className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight whitespace-nowrap">
            FreeMusic<span className="hidden sm:inline">.Top</span>
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* 曲库入口 */}
          <a
            href="/tracks"
            className="hidden sm:flex items-center gap-1.5 text-sm text-gray-600 hover:text-indigo-600 font-medium transition px-3 py-1.5 rounded-lg hover:bg-gray-100"
          >
            🎵 {t(lang, "trackLibrary")}
          </a>

          {/* 氛围歌单入口 */}
          <a
            href="/ambient"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 font-medium transition px-3 py-1.5 rounded-lg hover:bg-indigo-50"
          >
            🎨 {t(lang, "scenePlaylistNav")}
          </a>

          {/* Mobile menu button */}
          <div className="relative sm:hidden" ref={mobileMenuRef}>
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-600 hover:text-indigo-600 hover:bg-gray-100 transition"
              aria-label="Menu"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {showMobileMenu ? (
                  <>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </>
                ) : (
                  <>
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </>
                )}
              </svg>
            </button>
            {showMobileMenu && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-[50] overflow-hidden">
                <a
                  href="/tracks"
                  onClick={() => setShowMobileMenu(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  🎵 {t(lang, "trackLibrary")}
                </a>
                <a
                  href="/ambient"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowMobileMenu(false)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-sm text-indigo-600 hover:bg-indigo-50 transition"
                >
                  🎨 {t(lang, "scenePlaylistNav")}
                </a>
              </div>
            )}
          </div>

          {/* Lang dropdown */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setShowLangMenu(!showLangMenu)}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition px-3 py-1.5 rounded-lg hover:bg-gray-100"
            >
              <span className="text-xl leading-none">{lang === "zh" ? "🇨🇳" : "🇺🇸"}</span>
            </button>
            {showLangMenu && (
              <div className="absolute right-0 top-full mt-2 w-44 bg-white rounded-xl shadow-xl border border-gray-100 px-2 py-2 z-[50] overflow-hidden">
                  <button
                    onClick={() => {
                      setShowLangMenu(false);
                      if (lang !== "zh") onToggleLang();
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition ${
                      lang === "zh"
                        ? "bg-indigo-50 text-indigo-600 font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="text-xl">🇨🇳</span>
                    <span>中文</span>
                  </button>
                  <div className="border-t border-gray-100" />
                  <button
                    onClick={() => {
                      setShowLangMenu(false);
                      if (lang !== "en") onToggleLang();
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition ${
                      lang === "en"
                        ? "bg-indigo-50 text-indigo-600 font-medium"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span className="text-xl">🇺🇸</span>
                    <span>English</span>
                  </button>
                </div>
            )}
          </div>
          {user ? (
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt="avatar"
                    className="w-8 h-8 rounded-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                      e.currentTarget.nextSibling.style.display = "flex";
                    }}
                  />
                ) : null}
                <div
                  className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white text-sm font-bold"
                  style={{
                    display: user.picture ? "none" : "flex",
                  }}
                >
                  {userInitials}
                </div>
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-xl border border-gray-100 px-2 py-2 z-[50] overflow-hidden">
                    <button
                      onClick={() => onOpenUserLibrary("liked")}
                      className="w-full flex items-center gap-3.5 px-5 py-3.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-red-400"
                      >
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                      </svg>
                      {t(lang, "myLikes")}
                    </button>
                    <button
                      onClick={() => onOpenUserLibrary("favorited")}
                      className="w-full flex items-center gap-3.5 px-5 py-3.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-amber-400"
                      >
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                      {t(lang, "myFavorites")}
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        onNavigate("/playlists");
                      }}
                      className="w-full flex items-center gap-3.5 px-5 py-3.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-indigo-400"
                      >
                        <path d="M9 18V5l12-2v13" />
                        <circle cx="6" cy="18" r="3" />
                        <circle cx="18" cy="16" r="3" />
                      </svg>
                      {t(lang, "playlistManage")}
                    </button>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        onNavigate("/profile");
                      }}
                      className="w-full flex items-center gap-3.5 px-5 py-3.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                      {t(lang, "profile")}
                    </button>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => {
                            setShowUserMenu(false);
                            onNavigate("/admin");
                          }}
                          className="w-full flex items-center gap-3.5 px-5 py-3.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                          {t(lang, "adminPanel")}
                        </button>
                        <div className="border-t border-gray-100 my-1" />
                      </>
                    )}
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={onLogout}
                      className="w-full flex items-center gap-3.5 px-5 py-3.5 text-sm text-gray-500 hover:bg-gray-50 transition"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      {t(lang, "logout")}
                    </button>
                  </div>
              )}
            </div>
          ) : (
            <button
              onClick={onOpenEmailLogin}
              className="px-4 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium transition"
            >
              {t(lang, "login")}
            </button>
          )}
        </div>
      </div>
    </nav>
  );
}
